import { useState } from "react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Mail } from "lucide-react";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-2">
        <div className="h-10 w-24 bg-muted/20 animate-pulse rounded-lg" />
        <div className="h-10 w-24 bg-muted/20 animate-pulse rounded-lg" />
        <div className="h-10 w-24 bg-muted/20 animate-pulse rounded-lg" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-300 text-white hover:text-white/90 group overflow-hidden">
                <span className="relative z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-accent via-secondary to-accent transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        <NavigationMenuItem data-index={menuItems.length}>
          <NavigationMenuLink asChild>
            <Link to="#ejemplos-de-exito" className="relative inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-300 text-white hover:text-white/90 group overflow-hidden">
              <span className="relative z-10">Ganancias</span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-accent via-secondary to-accent transition-all duration-300 group-hover:w-full" />
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <Button variant="ghost" size="icon" className="lg:hidden" disabled>
        <Menu className="size-5" />
      </Button>;
  }
  return <>
      <Button variant="ghost" size="icon" className="lg:hidden relative z-50 hover:bg-white/10 hover:scale-110 transition-all duration-300" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        <div className="relative size-5">
          <Menu className={`size-5 absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
          <X className={`size-5 absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
        </div>
      </Button>

      <div className={`fixed inset-0 bg-background-dark/60 backdrop-blur-md lg:hidden transition-all duration-300 z-40 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsOpen(false)} />

      <div className={`fixed top-16 right-0 bottom-0 w-80 bg-background/98 backdrop-blur-xl border-l border-border shadow-strong lg:hidden z-40 transition-all duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="h-full overflow-y-auto px-6 py-8">
          <ul className="flex flex-col gap-3">
            <li data-index={0}>
              <Link to="/reserva" className="block" onClick={() => setIsOpen(false)}>
                <Button size="lg" className="w-full bg-gradient-to-r from-primary via-primary-hover to-primary text-primary-foreground hover:shadow-strong transition-all duration-300 font-bold text-base hover:scale-[1.02]" asChild>
                  <span>RESERVA TU LUGAR AQUI</span>
                </Button>
              </Link>
            </li>
            <div className="h-px bg-border my-2" />
            {menuItems.map((item, i) => <li key={item.id} data-index={i + 1}>
                <Link to={item.href || "#"} className="block px-5 py-3.5 rounded-lg text-base font-medium transition-all hover:bg-primary/5 hover:text-primary hover:translate-x-1 focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px] border border-transparent hover:border-primary/20" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
              </li>)}
            <li data-index={menuItems.length + 1}>
              <Link to="#ejemplos-de-exito" className="flex items-center px-5 py-3.5 rounded-lg text-base font-medium transition-all hover:bg-primary/5 hover:text-primary hover:translate-x-1 focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px] border border-transparent hover:border-primary/20" onClick={() => setIsOpen(false)}>
                Ejemplos de Éxito
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </>;
}
export default function Header() {
  return <header className="sticky top-0 z-50 w-full bg-black backdrop-blur-xl border-b border-border/50 shadow-soft">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/header_subtle_stock_chart_pattern.png)] opacity-10 pointer-events-none" />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/95 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-transparent to-black/90 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40 pointer-events-none" />
      
      <div className="container max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center focus-ring rounded-lg px-2 py-1 transition-all duration-300 hover:scale-105 group">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1401256/img/website_logo_zi.png" alt="ZI" className="h-7 transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" />
          </Link>

          <MenuProvider menu_id="17">
            <HeaderNav />
          </MenuProvider>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-4 border-r border-border/40 pr-6">
              <Link to="tel:+15551234567" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-all duration-300 focus-ring rounded-lg px-3 py-2 hover:bg-white/10 group">
                <Phone className="size-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" />
                <span className="font-medium">321-247-1391</span>
              </Link>
              <Link to="mailto:contacto@zafiroinvestment.com" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-all duration-300 focus-ring rounded-lg px-3 py-2 hover:bg-white/10 group">
                <Mail className="size-4 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-12" />
                <span className="font-medium">contacto@zafiroinvestment.com</span>
              </Link>
            </div>

            <Link to="/reserva">
              <Button size="lg" className="bg-gradient-to-r from-primary via-primary-hover to-primary text-primary-foreground hover:shadow-strong transition-all duration-300 font-bold hover:-translate-y-1 hover:scale-105 shadow-medium relative overflow-hidden group">
                <span className="relative z-10">RESERVA TU LUGAR AQUI</span>
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </Button>
            </Link>
          </div>

          <MenuProvider menu_id="17">
            <MobileNav />
          </MenuProvider>
        </div>
      </div>
    </header>;
}